/* eslint-disable */
import Long from "long";
import _m0 from "protobufjs/minimal";
import { Timestamp } from "../../google/protobuf/timestamp";
import { DiscoveryResponse } from "../../pkg/discovery/discovery";

export const protobufPackage = "continuum_rpc";

export enum BufferSource {
  unspecified = 0,
  stderr = 1,
  stdout = 2,
  journalctl = 3,
  windows_event_log = 4,
  /**
   * applog - applog: emitted from INSIDE the process by its own logger, as opposed to
   * captured from its stdout/stderr (stdout/stderr) or from the OS's view of it
   * (journalctl/windows_event_log). Same axis, one more source.
   */
  applog = 5,
  UNRECOGNIZED = -1,
}

export function bufferSourceFromJSON(object: any): BufferSource {
  switch (object) {
    case 0:
    case "unspecified":
      return BufferSource.unspecified;
    case 1:
    case "stderr":
      return BufferSource.stderr;
    case 2:
    case "stdout":
      return BufferSource.stdout;
    case 3:
    case "journalctl":
      return BufferSource.journalctl;
    case 4:
    case "windows_event_log":
      return BufferSource.windows_event_log;
    case 5:
    case "applog":
      return BufferSource.applog;
    case -1:
    case "UNRECOGNIZED":
    default:
      return BufferSource.UNRECOGNIZED;
  }
}

export function bufferSourceToJSON(object: BufferSource): string {
  switch (object) {
    case BufferSource.unspecified:
      return "unspecified";
    case BufferSource.stderr:
      return "stderr";
    case BufferSource.stdout:
      return "stdout";
    case BufferSource.journalctl:
      return "journalctl";
    case BufferSource.windows_event_log:
      return "windows_event_log";
    case BufferSource.applog:
      return "applog";
    case BufferSource.UNRECOGNIZED:
    default:
      return "UNRECOGNIZED";
  }
}

/**
 * LogLevel mirrors log/log.go's five levels. Deliberately NOT the LogLevel in
 * pkg/rpc/process/process_rpc.proto: that one is unused, lacks trace, and importing
 * it would couple continuum_rpc to process_rpc for nothing.
 */
export enum LogLevel {
  level_unspecified = 0,
  trace = 1,
  debug = 2,
  info = 3,
  warn = 4,
  error = 5,
  UNRECOGNIZED = -1,
}

export function logLevelFromJSON(object: any): LogLevel {
  switch (object) {
    case 0:
    case "level_unspecified":
      return LogLevel.level_unspecified;
    case 1:
    case "trace":
      return LogLevel.trace;
    case 2:
    case "debug":
      return LogLevel.debug;
    case 3:
    case "info":
      return LogLevel.info;
    case 4:
    case "warn":
      return LogLevel.warn;
    case 5:
    case "error":
      return LogLevel.error;
    case -1:
    case "UNRECOGNIZED":
    default:
      return LogLevel.UNRECOGNIZED;
  }
}

export function logLevelToJSON(object: LogLevel): string {
  switch (object) {
    case LogLevel.level_unspecified:
      return "level_unspecified";
    case LogLevel.trace:
      return "trace";
    case LogLevel.debug:
      return "debug";
    case LogLevel.info:
      return "info";
    case LogLevel.warn:
      return "warn";
    case LogLevel.error:
      return "error";
    case LogLevel.UNRECOGNIZED:
    default:
      return "UNRECOGNIZED";
  }
}

export enum SystemdServiceStatus {
  SystemdServiceStatus_Unspecified = 0,
  SystemdServiceStatus_Stopped = 1,
  SystemdServiceStatus_Running = 2,
  SystemdServiceStatus_Failed = 3,
  SystemdServiceStatus_Unknown = 4,
  UNRECOGNIZED = -1,
}

export function systemdServiceStatusFromJSON(object: any): SystemdServiceStatus {
  switch (object) {
    case 0:
    case "SystemdServiceStatus_Unspecified":
      return SystemdServiceStatus.SystemdServiceStatus_Unspecified;
    case 1:
    case "SystemdServiceStatus_Stopped":
      return SystemdServiceStatus.SystemdServiceStatus_Stopped;
    case 2:
    case "SystemdServiceStatus_Running":
      return SystemdServiceStatus.SystemdServiceStatus_Running;
    case 3:
    case "SystemdServiceStatus_Failed":
      return SystemdServiceStatus.SystemdServiceStatus_Failed;
    case 4:
    case "SystemdServiceStatus_Unknown":
      return SystemdServiceStatus.SystemdServiceStatus_Unknown;
    case -1:
    case "UNRECOGNIZED":
    default:
      return SystemdServiceStatus.UNRECOGNIZED;
  }
}

export function systemdServiceStatusToJSON(object: SystemdServiceStatus): string {
  switch (object) {
    case SystemdServiceStatus.SystemdServiceStatus_Unspecified:
      return "SystemdServiceStatus_Unspecified";
    case SystemdServiceStatus.SystemdServiceStatus_Stopped:
      return "SystemdServiceStatus_Stopped";
    case SystemdServiceStatus.SystemdServiceStatus_Running:
      return "SystemdServiceStatus_Running";
    case SystemdServiceStatus.SystemdServiceStatus_Failed:
      return "SystemdServiceStatus_Failed";
    case SystemdServiceStatus.SystemdServiceStatus_Unknown:
      return "SystemdServiceStatus_Unknown";
    case SystemdServiceStatus.UNRECOGNIZED:
    default:
      return "UNRECOGNIZED";
  }
}

export interface ProcessStartedRequest {
  processUid: string;
  parentProcessRunUid: string;
  processPid: number;
  workerUid: string;
  /** the job this run belongs to (renamed from serviceUid; field number 5 unchanged for wire compat) */
  jobUid: string;
  /** UTC timestamp */
  startedAt: Date | undefined;
  command: string[];
  cwd: string;
  env: string[];
  category: string;
  channels: string[];
  controllable: boolean;
  launchError: string;
  kind: string;
  extraDataJsonStr: string;
  /** the job name this run announces (renamed from serviceName; field number 16 unchanged for wire compat) */
  jobName: string;
  ephemeralName: string;
  /** Scheduler event UID for correlation */
  eventUid: string;
  /** Embedded discovery information (eliminates need for separate RPC query) */
  discovery:
    | DiscoveryResponse
    | undefined;
  /**
   * is_update marks this message as a refresh of an already-known process
   * rather than a first arrival. The consumer overwrites the mutable
   * snapshot fields (rpc schema list, mailbox address, location, engines,
   * metadata) but leaves immutable identity fields (started_at, command,
   * cwd, kind, …) on the existing processrun row untouched. If no
   * existing row is found the consumer falls through to first-arrival
   * behavior so the message is self-healing.
   */
  isUpdate: boolean;
  /**
   * Self-detected process manager (FEATURE-20260714-self-detect-process-manager):
   * the process reports, about ITSELF, who supervises it and under what name.
   * processManagerUnit is the CONTROL HANDLE — the systemd unit
   * ("a8-jobrun-<id>.service") or supervisord program ("group:program") you pass
   * to that manager to start/stop/restart it. So a job's unit is resolvable from
   * its processrun with no `a8 svc import`. Also carried in discovery.metadata
   * (process_manager*) for map-based consumers; these are the first-class form.
   * All empty when unmanaged (bare CLI / non-systemd) — normal, not an error.
   */
  processManager: string;
  /** unit / program name (the control handle) */
  processManagerUnit: string;
  /** "system" | "user" (systemd only) */
  processManagerScope: string;
}

export interface ProcessPingRequest {
  pings: ProcessPing[];
  /** UTC timestamp */
  timestamp: Date | undefined;
}

export interface ProcessPing {
  processUid: string;
  channelSizes: { [key: string]: string };
  /**
   * status reports liveness state: "" / "running" = actively working,
   * "paused" = alive but yielded/checkpointed (e.g. a paused checkpoint program).
   * A paused process keeps pinging so it is NOT AWOL-swept, but is not "running".
   */
  status: string;
}

export interface ProcessPing_ChannelSizesEntry {
  key: string;
  value: string;
}

export interface ProcessCompletedRequest {
  processUid: string;
  exitCode: number;
  exitMessage: string;
  /** UTC timestamp */
  completedAt:
    | Date
    | undefined;
  /**
   * Scheduler event UID for correlation (mirrors ProcessStartedRequest.eventUid).
   * Carried on completion so the registry can resolve the lifecycle bundle's
   * schedule/event DIRECTLY instead of looking up the jobevent by process_run_uid —
   * which races for a REMOTE worker (the leaf's completion can reach the registry
   * before continuum commits the jobevent.process_run_uid link, yielding an empty
   * schedule/event on run.completed and breaking checker correlation).
   */
  eventUid: string;
}

/**
 * MetricsPushed announces that a LIVE metrics pusher (a resident collector, e.g.
 * `a8 worker capture-metrics --interval ...`) committed one fresh metrics row for
 * its processrun. The pusher sends this only AFTER its insert RPC succeeded (the
 * same publish-after-commit race barrier the lifecycle contract requires), so the
 * registry can assemble a self-contained metrics.pushed lifecycle event from the
 * latest committed row. A resident pusher never completes, so this — not
 * ProcessCompleted — is its per-capture checker trigger.
 */
export interface MetricsPushedRequest {
  processUid: string;
  workerUid: string;
  jobUid: string;
  /**
   * The capture's own occurrence time (epoch millis). Rides into the lifecycle
   * envelope's dedup key so distinct pushes from one process never dedup away.
   */
  occurredAtEpochMs: string;
}

export interface Ack {
}

/**
 * ArchiveNow triggers ONE full log-archive pass on demand (the archiver runs it,
 * single writer — no race with the scheduled loop). Used by the CLI purge to
 * archive freshly-completed procs before a purge pass so they become eligible.
 */
export interface ArchiveNowRequest {
}

export interface ArchiveNowResponse {
  /** completed procs marked done this pass */
  processesArchived: number;
}

export interface MessageFromRunner {
  processStartedRequest?: ProcessStartedRequest | undefined;
  processPingRequest?: ProcessPingRequest | undefined;
  processCompletedRequest?: ProcessCompletedRequest | undefined;
  streamWrite?: StreamWrite | undefined;
  pingRequest?: PingRequest | undefined;
  mailboxPing?: MailboxPing | undefined;
  streamCreatedRequest?: StreamCreatedRequest | undefined;
  metricsPushedRequest?: MetricsPushedRequest | undefined;
}

/**
 * StreamCreatedRequest is a leaf's notification, sent the moment it creates a
 * processrun channel stream (console/journal) in NATS, so the server writes the
 * matching continuum.stream record. Active registration by design: we create a
 * NATS stream, we create its record — no NATS-sweeping to reconcile. The leaf
 * has no pg; the server (which holds the DB) is the sole writer of the stream
 * ledger. stream_name/subject are derived server-side from processUid+channelName.
 */
export interface StreamCreatedRequest {
  processUid: string;
  channelName: string;
  /** R the leaf created the stream with (processrun logs = 1) */
  replicas: number;
  /** the n1..nX tag the leaf placed the R=1 stream on ("" if unplaced / single-node); recorded on the stream record */
  placementTag: string;
}

/**
 * MailboxPing is a leaf mailbox's fire-and-forget aliveness signal. Leaves
 * publish it on continuum.central instead of round-tripping the mailbox-records
 * update endpoint with a full record — the server touches LastActivity
 * through its store (cache-coherent, debounced server-side).
 */
export interface MailboxPing {
  adminKey: string;
}

export interface StreamWrite {
  processUid: string;
  channelName: string;
  record: StreamRecord | undefined;
}

export interface StreamRecord {
  /** UTC timestamp */
  timestamp: Date | undefined;
  sequence: string;
  buffers: Buffer[];
  /**
   * writerEpoch identifies the writer INSTANCE (stamped once at construction).
   * sequence is contiguous only within one writer's life and resets to 1 for a
   * fresh writer (e.g. a job retry reusing the same processUid) — an epoch
   * change marks a reset legitimate; a sequence skip WITHIN an epoch is real
   * loss. Enables the gap detection that JetStream stream sequences cannot
   * provide (per-subject they are gappy by construction on shared streams).
   */
  writerEpoch: string;
  /**
   * eof marks the writer's terminal record: the writer closed cleanly and no
   * further records follow from this epoch. Emitted synchronously as the last
   * record on shutdown. Without this marker a dropped FINAL record is
   * undetectable — nothing follows it to reveal the skip.
   */
  eof: boolean;
  /**
   * finalSequence (on eof records) is the last data-carrying sequence this
   * writer emitted: holding contiguous 1..finalSequence for the epoch == no
   * loss. The archiver can reconcile against this at run completion.
   */
  finalSequence: string;
}

export interface Buffer {
  /** UTC timestamp */
  timestamp: Date | undefined;
  data: Uint8Array;
  source: BufferSource;
  /**
   * record is the STRUCTURED payload, carried by the applog channel (a process
   * logging its own log/log.go events). console/journal leave it unset and carry
   * opaque bytes in `data` — those are a byte stream with no level or caller to
   * capture, and wrapping them here would only mint permanently-empty fields.
   * Both payloads ride the same StreamRecord envelope, so ordering, epoch/EOF gap
   * detection, archiving, and reaping are one mechanism rather than two.
   */
  record: LogRecord | undefined;
}

/**
 * LogRecord is one structured log event from a process's own logger — the record
 * log/log.go builds and then currently destroys by Sprintf'ing every field into a
 * single string. Keeping it structured is what lets a reader filter by level or
 * caller instead of substring-matching a rendered line.
 */
export interface LogRecord {
  level: LogLevel;
  /** "a8nats.go:816" */
  caller: string;
  /** goroutine id */
  goid: string;
  /** the formatted message */
  message: string;
  /**
   * extraData is the structured-logging bag: request_id, job_uid, mailbox, etc.
   * Fields the Sprintf-to-string format cannot express at all. Same shape as
   * registry ProcessInfo.ExtendedMetadata, which is the existing precedent.
   */
  extraData: { [key: string]: string };
  /**
   * rendered is the exact human line the process's own console showed. Sent
   * ALONGSIDE the structured fields during rollout so a reader that has not
   * learned LogRecord yet still prints something correct, and so a structured
   * viewer can fall back to the writer's own formatting. Once every reader
   * decodes the fields this can stop being populated — the structure is the
   * source of truth, this is the compatibility shim.
   */
  rendered: string;
  /**
   * droppedRecords, when > 0, is how many records the WRITER's spool had to drop
   * before this one (NATS unreachable long enough to overflow). Carried on the
   * wire so the loss is VISIBLE to a reader — a truncated log that admits what
   * it lost beats one that quietly shows a hole.
   */
  droppedRecords: string;
  /**
   * error is the ERROR ITSELF, kept structured instead of flattened into message.
   * Unset on non-error records.
   *
   * Both languages already HAVE this and both currently throw it away at the last
   * moment — the same crime the level suffered. Go's log.ErrorE(err) takes a real
   * error (routinely a stacktrace.Propagate chain carrying file/line frames) and
   * immediately does err.Error(); the JVM attaches a Throwable that logback renders
   * to a stack trace. In both cases the most valuable payload in the whole record —
   * what broke and where — survives only as a substring.
   */
  error:
    | LogError
    | undefined;
  /**
   * thread is the JVM's concurrent-context identity (the thread name: "main",
   * "nats-worker-3"). It is the JVM's answer to the same question `goid` answers
   * for Go, and the two are deliberately SEPARATE fields: a thread name is a
   * string and a goroutine id is a number, so each language fills its own and
   * neither has to fake or stringify the other's. Exactly one is populated.
   */
  thread: string;
  /**
   * processRunUid is the processrun whose OS process produced this record, set when
   * that is not the run the stream belongs to: a checkpoint process's console belongs
   * to its overarching run and is written by each processrun that runs a leg of it.
   * Unset means the stream's own processrun.
   */
  processRunUid: string;
}

export interface LogRecord_ExtraDataEntry {
  key: string;
  value: string;
}

/**
 * LogError is a logged error, with its chain intact. The `cause` link is what
 * makes it worth being a message rather than a string: a wrapped error (Go's
 * stacktrace.Propagate / fmt.Errorf %w, the JVM's getCause) is a CHAIN, and the
 * root cause — the interesting one — is at the far end of it. Flattening it to
 * text is what forces a human to read a wall of prose to find the bottom frame.
 */
export interface LogError {
  /** message is this link's own message (err.Error() / Throwable.getMessage()). */
  message: string;
  /**
   * type is the concrete error/exception type ("*net.OpError",
   * "java.io.IOException"). A FIELD, so "show me every IOException in the fleet"
   * is a filter rather than a substring guess.
   */
  type: string;
  /** stack is the rendered stack trace / propagation frames for this link. */
  stack: string;
  /** cause is the wrapped error, recursively. Nil at the root. */
  cause: LogError | undefined;
}

export interface ListServicesRequest {
  workerUid: string;
}

export interface ListServicesResponse {
  records: ServiceRecord[];
}

export interface ServiceRecord {
  uid: string;
  name: string;
  workerUid: string;
  workerEnabled: boolean;
  unitJson: ServiceUnitJson | undefined;
}

export interface PingRequest {
  uid: string;
  payload: string;
}

export interface ServiceUnitJson {
  status: SystemdServiceStatus;
  statusSource: string;
  unitExists: boolean;
  unitDescription: string;
  unitName: string;
}

export interface ResolveJobUidRequest {
  jobName: string;
  workerUid: string;
  /** the job.kind to create if absent: "worker" (a8 worker run) or "supervisor" (service-manager/systemd). Empty -> supervisor. */
  kind: string;
}

export interface ResolveJobUidResponse {
  jobUid: string;
}

/**
 * AuthorizeForJob is the per-job authorization oracle. A server-side agent
 * (e.g. the service-manager agent) calls this BEFORE performing a privileged action,
 * passing the ORIGINAL operator's mailbox (read from its own verified rpcCtx)
 * so continuum authorizes the operator — not the agent — against the job's
 * ACL. continuum evaluates the SAME jobauth.Checker predicate used by
 * scheduler.RunJobNow. Returns a binary allow/deny; the agent enforces.
 */
export interface AuthorizeForJobRequest {
  /** the operator to authorize (NOT the RPC sender) */
  callerMailbox: string;
  /** the job the action targets */
  jobUid: string;
  /** jobauth rank (RankRun=2 for start/stop/restart) */
  requiredRank: number;
}

export interface AuthorizeForJobResponse {
  allowed: boolean;
  /** human-readable denial reason when !allowed */
  reason: string;
}

export interface ArchivedNatsMessage {
  /** stream sequence number */
  sequence: string;
  /** message timestamp from NATS */
  timestamp:
    | Date
    | undefined;
  /** NATS subject */
  subject: string;
  /** raw message payload */
  data: Uint8Array;
  /** NATS headers */
  headers: { [key: string]: string };
}

export interface ArchivedNatsMessage_HeadersEntry {
  key: string;
  value: string;
}

function createBaseProcessStartedRequest(): ProcessStartedRequest {
  return {
    processUid: "",
    parentProcessRunUid: "",
    processPid: 0,
    workerUid: "",
    jobUid: "",
    startedAt: undefined,
    command: [],
    cwd: "",
    env: [],
    category: "",
    channels: [],
    controllable: false,
    launchError: "",
    kind: "",
    extraDataJsonStr: "",
    jobName: "",
    ephemeralName: "",
    eventUid: "",
    discovery: undefined,
    isUpdate: false,
    processManager: "",
    processManagerUnit: "",
    processManagerScope: "",
  };
}

export const ProcessStartedRequest = {
  encode(message: ProcessStartedRequest, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.processUid !== "") {
      writer.uint32(10).string(message.processUid);
    }
    if (message.parentProcessRunUid !== "") {
      writer.uint32(18).string(message.parentProcessRunUid);
    }
    if (message.processPid !== 0) {
      writer.uint32(24).int32(message.processPid);
    }
    if (message.workerUid !== "") {
      writer.uint32(34).string(message.workerUid);
    }
    if (message.jobUid !== "") {
      writer.uint32(42).string(message.jobUid);
    }
    if (message.startedAt !== undefined) {
      Timestamp.encode(toTimestamp(message.startedAt), writer.uint32(50).fork()).ldelim();
    }
    for (const v of message.command) {
      writer.uint32(58).string(v!);
    }
    if (message.cwd !== "") {
      writer.uint32(66).string(message.cwd);
    }
    for (const v of message.env) {
      writer.uint32(74).string(v!);
    }
    if (message.category !== "") {
      writer.uint32(82).string(message.category);
    }
    for (const v of message.channels) {
      writer.uint32(90).string(v!);
    }
    if (message.controllable === true) {
      writer.uint32(96).bool(message.controllable);
    }
    if (message.launchError !== "") {
      writer.uint32(106).string(message.launchError);
    }
    if (message.kind !== "") {
      writer.uint32(114).string(message.kind);
    }
    if (message.extraDataJsonStr !== "") {
      writer.uint32(122).string(message.extraDataJsonStr);
    }
    if (message.jobName !== "") {
      writer.uint32(130).string(message.jobName);
    }
    if (message.ephemeralName !== "") {
      writer.uint32(138).string(message.ephemeralName);
    }
    if (message.eventUid !== "") {
      writer.uint32(146).string(message.eventUid);
    }
    if (message.discovery !== undefined) {
      DiscoveryResponse.encode(message.discovery, writer.uint32(154).fork()).ldelim();
    }
    if (message.isUpdate === true) {
      writer.uint32(160).bool(message.isUpdate);
    }
    if (message.processManager !== "") {
      writer.uint32(170).string(message.processManager);
    }
    if (message.processManagerUnit !== "") {
      writer.uint32(178).string(message.processManagerUnit);
    }
    if (message.processManagerScope !== "") {
      writer.uint32(186).string(message.processManagerScope);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): ProcessStartedRequest {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseProcessStartedRequest();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.processUid = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.parentProcessRunUid = reader.string();
          continue;
        case 3:
          if (tag !== 24) {
            break;
          }

          message.processPid = reader.int32();
          continue;
        case 4:
          if (tag !== 34) {
            break;
          }

          message.workerUid = reader.string();
          continue;
        case 5:
          if (tag !== 42) {
            break;
          }

          message.jobUid = reader.string();
          continue;
        case 6:
          if (tag !== 50) {
            break;
          }

          message.startedAt = fromTimestamp(Timestamp.decode(reader, reader.uint32()));
          continue;
        case 7:
          if (tag !== 58) {
            break;
          }

          message.command.push(reader.string());
          continue;
        case 8:
          if (tag !== 66) {
            break;
          }

          message.cwd = reader.string();
          continue;
        case 9:
          if (tag !== 74) {
            break;
          }

          message.env.push(reader.string());
          continue;
        case 10:
          if (tag !== 82) {
            break;
          }

          message.category = reader.string();
          continue;
        case 11:
          if (tag !== 90) {
            break;
          }

          message.channels.push(reader.string());
          continue;
        case 12:
          if (tag !== 96) {
            break;
          }

          message.controllable = reader.bool();
          continue;
        case 13:
          if (tag !== 106) {
            break;
          }

          message.launchError = reader.string();
          continue;
        case 14:
          if (tag !== 114) {
            break;
          }

          message.kind = reader.string();
          continue;
        case 15:
          if (tag !== 122) {
            break;
          }

          message.extraDataJsonStr = reader.string();
          continue;
        case 16:
          if (tag !== 130) {
            break;
          }

          message.jobName = reader.string();
          continue;
        case 17:
          if (tag !== 138) {
            break;
          }

          message.ephemeralName = reader.string();
          continue;
        case 18:
          if (tag !== 146) {
            break;
          }

          message.eventUid = reader.string();
          continue;
        case 19:
          if (tag !== 154) {
            break;
          }

          message.discovery = DiscoveryResponse.decode(reader, reader.uint32());
          continue;
        case 20:
          if (tag !== 160) {
            break;
          }

          message.isUpdate = reader.bool();
          continue;
        case 21:
          if (tag !== 170) {
            break;
          }

          message.processManager = reader.string();
          continue;
        case 22:
          if (tag !== 178) {
            break;
          }

          message.processManagerUnit = reader.string();
          continue;
        case 23:
          if (tag !== 186) {
            break;
          }

          message.processManagerScope = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): ProcessStartedRequest {
    return {
      processUid: isSet(object.processUid) ? globalThis.String(object.processUid) : "",
      parentProcessRunUid: isSet(object.parentProcessRunUid) ? globalThis.String(object.parentProcessRunUid) : "",
      processPid: isSet(object.processPid) ? globalThis.Number(object.processPid) : 0,
      workerUid: isSet(object.workerUid) ? globalThis.String(object.workerUid) : "",
      jobUid: isSet(object.jobUid) ? globalThis.String(object.jobUid) : "",
      startedAt: isSet(object.startedAt) ? fromJsonTimestamp(object.startedAt) : undefined,
      command: globalThis.Array.isArray(object?.command) ? object.command.map((e: any) => globalThis.String(e)) : [],
      cwd: isSet(object.cwd) ? globalThis.String(object.cwd) : "",
      env: globalThis.Array.isArray(object?.env) ? object.env.map((e: any) => globalThis.String(e)) : [],
      category: isSet(object.category) ? globalThis.String(object.category) : "",
      channels: globalThis.Array.isArray(object?.channels) ? object.channels.map((e: any) => globalThis.String(e)) : [],
      controllable: isSet(object.controllable) ? globalThis.Boolean(object.controllable) : false,
      launchError: isSet(object.launchError) ? globalThis.String(object.launchError) : "",
      kind: isSet(object.kind) ? globalThis.String(object.kind) : "",
      extraDataJsonStr: isSet(object.extraDataJsonStr) ? globalThis.String(object.extraDataJsonStr) : "",
      jobName: isSet(object.jobName) ? globalThis.String(object.jobName) : "",
      ephemeralName: isSet(object.ephemeralName) ? globalThis.String(object.ephemeralName) : "",
      eventUid: isSet(object.eventUid) ? globalThis.String(object.eventUid) : "",
      discovery: isSet(object.discovery) ? DiscoveryResponse.fromJSON(object.discovery) : undefined,
      isUpdate: isSet(object.isUpdate) ? globalThis.Boolean(object.isUpdate) : false,
      processManager: isSet(object.processManager) ? globalThis.String(object.processManager) : "",
      processManagerUnit: isSet(object.processManagerUnit) ? globalThis.String(object.processManagerUnit) : "",
      processManagerScope: isSet(object.processManagerScope) ? globalThis.String(object.processManagerScope) : "",
    };
  },

  toJSON(message: ProcessStartedRequest): unknown {
    const obj: any = {};
    if (message.processUid !== "") {
      obj.processUid = message.processUid;
    }
    if (message.parentProcessRunUid !== "") {
      obj.parentProcessRunUid = message.parentProcessRunUid;
    }
    if (message.processPid !== 0) {
      obj.processPid = Math.round(message.processPid);
    }
    if (message.workerUid !== "") {
      obj.workerUid = message.workerUid;
    }
    if (message.jobUid !== "") {
      obj.jobUid = message.jobUid;
    }
    if (message.startedAt !== undefined) {
      obj.startedAt = message.startedAt.toISOString();
    }
    if (message.command?.length) {
      obj.command = message.command;
    }
    if (message.cwd !== "") {
      obj.cwd = message.cwd;
    }
    if (message.env?.length) {
      obj.env = message.env;
    }
    if (message.category !== "") {
      obj.category = message.category;
    }
    if (message.channels?.length) {
      obj.channels = message.channels;
    }
    if (message.controllable === true) {
      obj.controllable = message.controllable;
    }
    if (message.launchError !== "") {
      obj.launchError = message.launchError;
    }
    if (message.kind !== "") {
      obj.kind = message.kind;
    }
    if (message.extraDataJsonStr !== "") {
      obj.extraDataJsonStr = message.extraDataJsonStr;
    }
    if (message.jobName !== "") {
      obj.jobName = message.jobName;
    }
    if (message.ephemeralName !== "") {
      obj.ephemeralName = message.ephemeralName;
    }
    if (message.eventUid !== "") {
      obj.eventUid = message.eventUid;
    }
    if (message.discovery !== undefined) {
      obj.discovery = DiscoveryResponse.toJSON(message.discovery);
    }
    if (message.isUpdate === true) {
      obj.isUpdate = message.isUpdate;
    }
    if (message.processManager !== "") {
      obj.processManager = message.processManager;
    }
    if (message.processManagerUnit !== "") {
      obj.processManagerUnit = message.processManagerUnit;
    }
    if (message.processManagerScope !== "") {
      obj.processManagerScope = message.processManagerScope;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<ProcessStartedRequest>, I>>(base?: I): ProcessStartedRequest {
    return ProcessStartedRequest.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<ProcessStartedRequest>, I>>(object: I): ProcessStartedRequest {
    const message = createBaseProcessStartedRequest();
    message.processUid = object.processUid ?? "";
    message.parentProcessRunUid = object.parentProcessRunUid ?? "";
    message.processPid = object.processPid ?? 0;
    message.workerUid = object.workerUid ?? "";
    message.jobUid = object.jobUid ?? "";
    message.startedAt = object.startedAt ?? undefined;
    message.command = object.command?.map((e) => e) || [];
    message.cwd = object.cwd ?? "";
    message.env = object.env?.map((e) => e) || [];
    message.category = object.category ?? "";
    message.channels = object.channels?.map((e) => e) || [];
    message.controllable = object.controllable ?? false;
    message.launchError = object.launchError ?? "";
    message.kind = object.kind ?? "";
    message.extraDataJsonStr = object.extraDataJsonStr ?? "";
    message.jobName = object.jobName ?? "";
    message.ephemeralName = object.ephemeralName ?? "";
    message.eventUid = object.eventUid ?? "";
    message.discovery = (object.discovery !== undefined && object.discovery !== null)
      ? DiscoveryResponse.fromPartial(object.discovery)
      : undefined;
    message.isUpdate = object.isUpdate ?? false;
    message.processManager = object.processManager ?? "";
    message.processManagerUnit = object.processManagerUnit ?? "";
    message.processManagerScope = object.processManagerScope ?? "";
    return message;
  },
};

function createBaseProcessPingRequest(): ProcessPingRequest {
  return { pings: [], timestamp: undefined };
}

export const ProcessPingRequest = {
  encode(message: ProcessPingRequest, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    for (const v of message.pings) {
      ProcessPing.encode(v!, writer.uint32(10).fork()).ldelim();
    }
    if (message.timestamp !== undefined) {
      Timestamp.encode(toTimestamp(message.timestamp), writer.uint32(18).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): ProcessPingRequest {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseProcessPingRequest();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.pings.push(ProcessPing.decode(reader, reader.uint32()));
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.timestamp = fromTimestamp(Timestamp.decode(reader, reader.uint32()));
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): ProcessPingRequest {
    return {
      pings: globalThis.Array.isArray(object?.pings) ? object.pings.map((e: any) => ProcessPing.fromJSON(e)) : [],
      timestamp: isSet(object.timestamp) ? fromJsonTimestamp(object.timestamp) : undefined,
    };
  },

  toJSON(message: ProcessPingRequest): unknown {
    const obj: any = {};
    if (message.pings?.length) {
      obj.pings = message.pings.map((e) => ProcessPing.toJSON(e));
    }
    if (message.timestamp !== undefined) {
      obj.timestamp = message.timestamp.toISOString();
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<ProcessPingRequest>, I>>(base?: I): ProcessPingRequest {
    return ProcessPingRequest.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<ProcessPingRequest>, I>>(object: I): ProcessPingRequest {
    const message = createBaseProcessPingRequest();
    message.pings = object.pings?.map((e) => ProcessPing.fromPartial(e)) || [];
    message.timestamp = object.timestamp ?? undefined;
    return message;
  },
};

function createBaseProcessPing(): ProcessPing {
  return { processUid: "", channelSizes: {}, status: "" };
}

export const ProcessPing = {
  encode(message: ProcessPing, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.processUid !== "") {
      writer.uint32(10).string(message.processUid);
    }
    Object.entries(message.channelSizes).forEach(([key, value]) => {
      ProcessPing_ChannelSizesEntry.encode({ key: key as any, value }, writer.uint32(18).fork()).ldelim();
    });
    if (message.status !== "") {
      writer.uint32(26).string(message.status);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): ProcessPing {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseProcessPing();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.processUid = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          const entry2 = ProcessPing_ChannelSizesEntry.decode(reader, reader.uint32());
          if (entry2.value !== undefined) {
            message.channelSizes[entry2.key] = entry2.value;
          }
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.status = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): ProcessPing {
    return {
      processUid: isSet(object.processUid) ? globalThis.String(object.processUid) : "",
      channelSizes: isObject(object.channelSizes)
        ? Object.entries(object.channelSizes).reduce<{ [key: string]: string }>((acc, [key, value]) => {
          acc[key] = String(value);
          return acc;
        }, {})
        : {},
      status: isSet(object.status) ? globalThis.String(object.status) : "",
    };
  },

  toJSON(message: ProcessPing): unknown {
    const obj: any = {};
    if (message.processUid !== "") {
      obj.processUid = message.processUid;
    }
    if (message.channelSizes) {
      const entries = Object.entries(message.channelSizes);
      if (entries.length > 0) {
        obj.channelSizes = {};
        entries.forEach(([k, v]) => {
          obj.channelSizes[k] = v;
        });
      }
    }
    if (message.status !== "") {
      obj.status = message.status;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<ProcessPing>, I>>(base?: I): ProcessPing {
    return ProcessPing.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<ProcessPing>, I>>(object: I): ProcessPing {
    const message = createBaseProcessPing();
    message.processUid = object.processUid ?? "";
    message.channelSizes = Object.entries(object.channelSizes ?? {}).reduce<{ [key: string]: string }>(
      (acc, [key, value]) => {
        if (value !== undefined) {
          acc[key] = globalThis.String(value);
        }
        return acc;
      },
      {},
    );
    message.status = object.status ?? "";
    return message;
  },
};

function createBaseProcessPing_ChannelSizesEntry(): ProcessPing_ChannelSizesEntry {
  return { key: "", value: "0" };
}

export const ProcessPing_ChannelSizesEntry = {
  encode(message: ProcessPing_ChannelSizesEntry, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.key !== "") {
      writer.uint32(10).string(message.key);
    }
    if (message.value !== "0") {
      writer.uint32(16).uint64(message.value);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): ProcessPing_ChannelSizesEntry {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseProcessPing_ChannelSizesEntry();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.key = reader.string();
          continue;
        case 2:
          if (tag !== 16) {
            break;
          }

          message.value = longToString(reader.uint64() as Long);
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): ProcessPing_ChannelSizesEntry {
    return {
      key: isSet(object.key) ? globalThis.String(object.key) : "",
      value: isSet(object.value) ? globalThis.String(object.value) : "0",
    };
  },

  toJSON(message: ProcessPing_ChannelSizesEntry): unknown {
    const obj: any = {};
    if (message.key !== "") {
      obj.key = message.key;
    }
    if (message.value !== "0") {
      obj.value = message.value;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<ProcessPing_ChannelSizesEntry>, I>>(base?: I): ProcessPing_ChannelSizesEntry {
    return ProcessPing_ChannelSizesEntry.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<ProcessPing_ChannelSizesEntry>, I>>(
    object: I,
  ): ProcessPing_ChannelSizesEntry {
    const message = createBaseProcessPing_ChannelSizesEntry();
    message.key = object.key ?? "";
    message.value = object.value ?? "0";
    return message;
  },
};

function createBaseProcessCompletedRequest(): ProcessCompletedRequest {
  return { processUid: "", exitCode: 0, exitMessage: "", completedAt: undefined, eventUid: "" };
}

export const ProcessCompletedRequest = {
  encode(message: ProcessCompletedRequest, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.processUid !== "") {
      writer.uint32(10).string(message.processUid);
    }
    if (message.exitCode !== 0) {
      writer.uint32(16).int32(message.exitCode);
    }
    if (message.exitMessage !== "") {
      writer.uint32(26).string(message.exitMessage);
    }
    if (message.completedAt !== undefined) {
      Timestamp.encode(toTimestamp(message.completedAt), writer.uint32(34).fork()).ldelim();
    }
    if (message.eventUid !== "") {
      writer.uint32(42).string(message.eventUid);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): ProcessCompletedRequest {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseProcessCompletedRequest();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.processUid = reader.string();
          continue;
        case 2:
          if (tag !== 16) {
            break;
          }

          message.exitCode = reader.int32();
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.exitMessage = reader.string();
          continue;
        case 4:
          if (tag !== 34) {
            break;
          }

          message.completedAt = fromTimestamp(Timestamp.decode(reader, reader.uint32()));
          continue;
        case 5:
          if (tag !== 42) {
            break;
          }

          message.eventUid = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): ProcessCompletedRequest {
    return {
      processUid: isSet(object.processUid) ? globalThis.String(object.processUid) : "",
      exitCode: isSet(object.exitCode) ? globalThis.Number(object.exitCode) : 0,
      exitMessage: isSet(object.exitMessage) ? globalThis.String(object.exitMessage) : "",
      completedAt: isSet(object.completedAt) ? fromJsonTimestamp(object.completedAt) : undefined,
      eventUid: isSet(object.eventUid) ? globalThis.String(object.eventUid) : "",
    };
  },

  toJSON(message: ProcessCompletedRequest): unknown {
    const obj: any = {};
    if (message.processUid !== "") {
      obj.processUid = message.processUid;
    }
    if (message.exitCode !== 0) {
      obj.exitCode = Math.round(message.exitCode);
    }
    if (message.exitMessage !== "") {
      obj.exitMessage = message.exitMessage;
    }
    if (message.completedAt !== undefined) {
      obj.completedAt = message.completedAt.toISOString();
    }
    if (message.eventUid !== "") {
      obj.eventUid = message.eventUid;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<ProcessCompletedRequest>, I>>(base?: I): ProcessCompletedRequest {
    return ProcessCompletedRequest.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<ProcessCompletedRequest>, I>>(object: I): ProcessCompletedRequest {
    const message = createBaseProcessCompletedRequest();
    message.processUid = object.processUid ?? "";
    message.exitCode = object.exitCode ?? 0;
    message.exitMessage = object.exitMessage ?? "";
    message.completedAt = object.completedAt ?? undefined;
    message.eventUid = object.eventUid ?? "";
    return message;
  },
};

function createBaseMetricsPushedRequest(): MetricsPushedRequest {
  return { processUid: "", workerUid: "", jobUid: "", occurredAtEpochMs: "0" };
}

export const MetricsPushedRequest = {
  encode(message: MetricsPushedRequest, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.processUid !== "") {
      writer.uint32(10).string(message.processUid);
    }
    if (message.workerUid !== "") {
      writer.uint32(18).string(message.workerUid);
    }
    if (message.jobUid !== "") {
      writer.uint32(26).string(message.jobUid);
    }
    if (message.occurredAtEpochMs !== "0") {
      writer.uint32(32).int64(message.occurredAtEpochMs);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): MetricsPushedRequest {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseMetricsPushedRequest();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.processUid = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.workerUid = reader.string();
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.jobUid = reader.string();
          continue;
        case 4:
          if (tag !== 32) {
            break;
          }

          message.occurredAtEpochMs = longToString(reader.int64() as Long);
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): MetricsPushedRequest {
    return {
      processUid: isSet(object.processUid) ? globalThis.String(object.processUid) : "",
      workerUid: isSet(object.workerUid) ? globalThis.String(object.workerUid) : "",
      jobUid: isSet(object.jobUid) ? globalThis.String(object.jobUid) : "",
      occurredAtEpochMs: isSet(object.occurredAtEpochMs) ? globalThis.String(object.occurredAtEpochMs) : "0",
    };
  },

  toJSON(message: MetricsPushedRequest): unknown {
    const obj: any = {};
    if (message.processUid !== "") {
      obj.processUid = message.processUid;
    }
    if (message.workerUid !== "") {
      obj.workerUid = message.workerUid;
    }
    if (message.jobUid !== "") {
      obj.jobUid = message.jobUid;
    }
    if (message.occurredAtEpochMs !== "0") {
      obj.occurredAtEpochMs = message.occurredAtEpochMs;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<MetricsPushedRequest>, I>>(base?: I): MetricsPushedRequest {
    return MetricsPushedRequest.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<MetricsPushedRequest>, I>>(object: I): MetricsPushedRequest {
    const message = createBaseMetricsPushedRequest();
    message.processUid = object.processUid ?? "";
    message.workerUid = object.workerUid ?? "";
    message.jobUid = object.jobUid ?? "";
    message.occurredAtEpochMs = object.occurredAtEpochMs ?? "0";
    return message;
  },
};

function createBaseAck(): Ack {
  return {};
}

export const Ack = {
  encode(_: Ack, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): Ack {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseAck();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(_: any): Ack {
    return {};
  },

  toJSON(_: Ack): unknown {
    const obj: any = {};
    return obj;
  },

  create<I extends Exact<DeepPartial<Ack>, I>>(base?: I): Ack {
    return Ack.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<Ack>, I>>(_: I): Ack {
    const message = createBaseAck();
    return message;
  },
};

function createBaseArchiveNowRequest(): ArchiveNowRequest {
  return {};
}

export const ArchiveNowRequest = {
  encode(_: ArchiveNowRequest, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): ArchiveNowRequest {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseArchiveNowRequest();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(_: any): ArchiveNowRequest {
    return {};
  },

  toJSON(_: ArchiveNowRequest): unknown {
    const obj: any = {};
    return obj;
  },

  create<I extends Exact<DeepPartial<ArchiveNowRequest>, I>>(base?: I): ArchiveNowRequest {
    return ArchiveNowRequest.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<ArchiveNowRequest>, I>>(_: I): ArchiveNowRequest {
    const message = createBaseArchiveNowRequest();
    return message;
  },
};

function createBaseArchiveNowResponse(): ArchiveNowResponse {
  return { processesArchived: 0 };
}

export const ArchiveNowResponse = {
  encode(message: ArchiveNowResponse, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.processesArchived !== 0) {
      writer.uint32(8).int32(message.processesArchived);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): ArchiveNowResponse {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseArchiveNowResponse();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 8) {
            break;
          }

          message.processesArchived = reader.int32();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): ArchiveNowResponse {
    return { processesArchived: isSet(object.processesArchived) ? globalThis.Number(object.processesArchived) : 0 };
  },

  toJSON(message: ArchiveNowResponse): unknown {
    const obj: any = {};
    if (message.processesArchived !== 0) {
      obj.processesArchived = Math.round(message.processesArchived);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<ArchiveNowResponse>, I>>(base?: I): ArchiveNowResponse {
    return ArchiveNowResponse.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<ArchiveNowResponse>, I>>(object: I): ArchiveNowResponse {
    const message = createBaseArchiveNowResponse();
    message.processesArchived = object.processesArchived ?? 0;
    return message;
  },
};

function createBaseMessageFromRunner(): MessageFromRunner {
  return {
    processStartedRequest: undefined,
    processPingRequest: undefined,
    processCompletedRequest: undefined,
    streamWrite: undefined,
    pingRequest: undefined,
    mailboxPing: undefined,
    streamCreatedRequest: undefined,
    metricsPushedRequest: undefined,
  };
}

export const MessageFromRunner = {
  encode(message: MessageFromRunner, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.processStartedRequest !== undefined) {
      ProcessStartedRequest.encode(message.processStartedRequest, writer.uint32(10).fork()).ldelim();
    }
    if (message.processPingRequest !== undefined) {
      ProcessPingRequest.encode(message.processPingRequest, writer.uint32(18).fork()).ldelim();
    }
    if (message.processCompletedRequest !== undefined) {
      ProcessCompletedRequest.encode(message.processCompletedRequest, writer.uint32(26).fork()).ldelim();
    }
    if (message.streamWrite !== undefined) {
      StreamWrite.encode(message.streamWrite, writer.uint32(34).fork()).ldelim();
    }
    if (message.pingRequest !== undefined) {
      PingRequest.encode(message.pingRequest, writer.uint32(50).fork()).ldelim();
    }
    if (message.mailboxPing !== undefined) {
      MailboxPing.encode(message.mailboxPing, writer.uint32(58).fork()).ldelim();
    }
    if (message.streamCreatedRequest !== undefined) {
      StreamCreatedRequest.encode(message.streamCreatedRequest, writer.uint32(66).fork()).ldelim();
    }
    if (message.metricsPushedRequest !== undefined) {
      MetricsPushedRequest.encode(message.metricsPushedRequest, writer.uint32(74).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): MessageFromRunner {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseMessageFromRunner();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.processStartedRequest = ProcessStartedRequest.decode(reader, reader.uint32());
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.processPingRequest = ProcessPingRequest.decode(reader, reader.uint32());
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.processCompletedRequest = ProcessCompletedRequest.decode(reader, reader.uint32());
          continue;
        case 4:
          if (tag !== 34) {
            break;
          }

          message.streamWrite = StreamWrite.decode(reader, reader.uint32());
          continue;
        case 6:
          if (tag !== 50) {
            break;
          }

          message.pingRequest = PingRequest.decode(reader, reader.uint32());
          continue;
        case 7:
          if (tag !== 58) {
            break;
          }

          message.mailboxPing = MailboxPing.decode(reader, reader.uint32());
          continue;
        case 8:
          if (tag !== 66) {
            break;
          }

          message.streamCreatedRequest = StreamCreatedRequest.decode(reader, reader.uint32());
          continue;
        case 9:
          if (tag !== 74) {
            break;
          }

          message.metricsPushedRequest = MetricsPushedRequest.decode(reader, reader.uint32());
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): MessageFromRunner {
    return {
      processStartedRequest: isSet(object.processStartedRequest)
        ? ProcessStartedRequest.fromJSON(object.processStartedRequest)
        : undefined,
      processPingRequest: isSet(object.processPingRequest)
        ? ProcessPingRequest.fromJSON(object.processPingRequest)
        : undefined,
      processCompletedRequest: isSet(object.processCompletedRequest)
        ? ProcessCompletedRequest.fromJSON(object.processCompletedRequest)
        : undefined,
      streamWrite: isSet(object.streamWrite) ? StreamWrite.fromJSON(object.streamWrite) : undefined,
      pingRequest: isSet(object.pingRequest) ? PingRequest.fromJSON(object.pingRequest) : undefined,
      mailboxPing: isSet(object.mailboxPing) ? MailboxPing.fromJSON(object.mailboxPing) : undefined,
      streamCreatedRequest: isSet(object.streamCreatedRequest)
        ? StreamCreatedRequest.fromJSON(object.streamCreatedRequest)
        : undefined,
      metricsPushedRequest: isSet(object.metricsPushedRequest)
        ? MetricsPushedRequest.fromJSON(object.metricsPushedRequest)
        : undefined,
    };
  },

  toJSON(message: MessageFromRunner): unknown {
    const obj: any = {};
    if (message.processStartedRequest !== undefined) {
      obj.processStartedRequest = ProcessStartedRequest.toJSON(message.processStartedRequest);
    }
    if (message.processPingRequest !== undefined) {
      obj.processPingRequest = ProcessPingRequest.toJSON(message.processPingRequest);
    }
    if (message.processCompletedRequest !== undefined) {
      obj.processCompletedRequest = ProcessCompletedRequest.toJSON(message.processCompletedRequest);
    }
    if (message.streamWrite !== undefined) {
      obj.streamWrite = StreamWrite.toJSON(message.streamWrite);
    }
    if (message.pingRequest !== undefined) {
      obj.pingRequest = PingRequest.toJSON(message.pingRequest);
    }
    if (message.mailboxPing !== undefined) {
      obj.mailboxPing = MailboxPing.toJSON(message.mailboxPing);
    }
    if (message.streamCreatedRequest !== undefined) {
      obj.streamCreatedRequest = StreamCreatedRequest.toJSON(message.streamCreatedRequest);
    }
    if (message.metricsPushedRequest !== undefined) {
      obj.metricsPushedRequest = MetricsPushedRequest.toJSON(message.metricsPushedRequest);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<MessageFromRunner>, I>>(base?: I): MessageFromRunner {
    return MessageFromRunner.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<MessageFromRunner>, I>>(object: I): MessageFromRunner {
    const message = createBaseMessageFromRunner();
    message.processStartedRequest =
      (object.processStartedRequest !== undefined && object.processStartedRequest !== null)
        ? ProcessStartedRequest.fromPartial(object.processStartedRequest)
        : undefined;
    message.processPingRequest = (object.processPingRequest !== undefined && object.processPingRequest !== null)
      ? ProcessPingRequest.fromPartial(object.processPingRequest)
      : undefined;
    message.processCompletedRequest =
      (object.processCompletedRequest !== undefined && object.processCompletedRequest !== null)
        ? ProcessCompletedRequest.fromPartial(object.processCompletedRequest)
        : undefined;
    message.streamWrite = (object.streamWrite !== undefined && object.streamWrite !== null)
      ? StreamWrite.fromPartial(object.streamWrite)
      : undefined;
    message.pingRequest = (object.pingRequest !== undefined && object.pingRequest !== null)
      ? PingRequest.fromPartial(object.pingRequest)
      : undefined;
    message.mailboxPing = (object.mailboxPing !== undefined && object.mailboxPing !== null)
      ? MailboxPing.fromPartial(object.mailboxPing)
      : undefined;
    message.streamCreatedRequest = (object.streamCreatedRequest !== undefined && object.streamCreatedRequest !== null)
      ? StreamCreatedRequest.fromPartial(object.streamCreatedRequest)
      : undefined;
    message.metricsPushedRequest = (object.metricsPushedRequest !== undefined && object.metricsPushedRequest !== null)
      ? MetricsPushedRequest.fromPartial(object.metricsPushedRequest)
      : undefined;
    return message;
  },
};

function createBaseStreamCreatedRequest(): StreamCreatedRequest {
  return { processUid: "", channelName: "", replicas: 0, placementTag: "" };
}

export const StreamCreatedRequest = {
  encode(message: StreamCreatedRequest, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.processUid !== "") {
      writer.uint32(10).string(message.processUid);
    }
    if (message.channelName !== "") {
      writer.uint32(18).string(message.channelName);
    }
    if (message.replicas !== 0) {
      writer.uint32(24).int32(message.replicas);
    }
    if (message.placementTag !== "") {
      writer.uint32(34).string(message.placementTag);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): StreamCreatedRequest {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseStreamCreatedRequest();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.processUid = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.channelName = reader.string();
          continue;
        case 3:
          if (tag !== 24) {
            break;
          }

          message.replicas = reader.int32();
          continue;
        case 4:
          if (tag !== 34) {
            break;
          }

          message.placementTag = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): StreamCreatedRequest {
    return {
      processUid: isSet(object.processUid) ? globalThis.String(object.processUid) : "",
      channelName: isSet(object.channelName) ? globalThis.String(object.channelName) : "",
      replicas: isSet(object.replicas) ? globalThis.Number(object.replicas) : 0,
      placementTag: isSet(object.placementTag) ? globalThis.String(object.placementTag) : "",
    };
  },

  toJSON(message: StreamCreatedRequest): unknown {
    const obj: any = {};
    if (message.processUid !== "") {
      obj.processUid = message.processUid;
    }
    if (message.channelName !== "") {
      obj.channelName = message.channelName;
    }
    if (message.replicas !== 0) {
      obj.replicas = Math.round(message.replicas);
    }
    if (message.placementTag !== "") {
      obj.placementTag = message.placementTag;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<StreamCreatedRequest>, I>>(base?: I): StreamCreatedRequest {
    return StreamCreatedRequest.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<StreamCreatedRequest>, I>>(object: I): StreamCreatedRequest {
    const message = createBaseStreamCreatedRequest();
    message.processUid = object.processUid ?? "";
    message.channelName = object.channelName ?? "";
    message.replicas = object.replicas ?? 0;
    message.placementTag = object.placementTag ?? "";
    return message;
  },
};

function createBaseMailboxPing(): MailboxPing {
  return { adminKey: "" };
}

export const MailboxPing = {
  encode(message: MailboxPing, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.adminKey !== "") {
      writer.uint32(10).string(message.adminKey);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): MailboxPing {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseMailboxPing();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.adminKey = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): MailboxPing {
    return { adminKey: isSet(object.adminKey) ? globalThis.String(object.adminKey) : "" };
  },

  toJSON(message: MailboxPing): unknown {
    const obj: any = {};
    if (message.adminKey !== "") {
      obj.adminKey = message.adminKey;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<MailboxPing>, I>>(base?: I): MailboxPing {
    return MailboxPing.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<MailboxPing>, I>>(object: I): MailboxPing {
    const message = createBaseMailboxPing();
    message.adminKey = object.adminKey ?? "";
    return message;
  },
};

function createBaseStreamWrite(): StreamWrite {
  return { processUid: "", channelName: "", record: undefined };
}

export const StreamWrite = {
  encode(message: StreamWrite, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.processUid !== "") {
      writer.uint32(10).string(message.processUid);
    }
    if (message.channelName !== "") {
      writer.uint32(18).string(message.channelName);
    }
    if (message.record !== undefined) {
      StreamRecord.encode(message.record, writer.uint32(26).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): StreamWrite {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseStreamWrite();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.processUid = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.channelName = reader.string();
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.record = StreamRecord.decode(reader, reader.uint32());
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): StreamWrite {
    return {
      processUid: isSet(object.processUid) ? globalThis.String(object.processUid) : "",
      channelName: isSet(object.channelName) ? globalThis.String(object.channelName) : "",
      record: isSet(object.record) ? StreamRecord.fromJSON(object.record) : undefined,
    };
  },

  toJSON(message: StreamWrite): unknown {
    const obj: any = {};
    if (message.processUid !== "") {
      obj.processUid = message.processUid;
    }
    if (message.channelName !== "") {
      obj.channelName = message.channelName;
    }
    if (message.record !== undefined) {
      obj.record = StreamRecord.toJSON(message.record);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<StreamWrite>, I>>(base?: I): StreamWrite {
    return StreamWrite.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<StreamWrite>, I>>(object: I): StreamWrite {
    const message = createBaseStreamWrite();
    message.processUid = object.processUid ?? "";
    message.channelName = object.channelName ?? "";
    message.record = (object.record !== undefined && object.record !== null)
      ? StreamRecord.fromPartial(object.record)
      : undefined;
    return message;
  },
};

function createBaseStreamRecord(): StreamRecord {
  return { timestamp: undefined, sequence: "0", buffers: [], writerEpoch: "0", eof: false, finalSequence: "0" };
}

export const StreamRecord = {
  encode(message: StreamRecord, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.timestamp !== undefined) {
      Timestamp.encode(toTimestamp(message.timestamp), writer.uint32(18).fork()).ldelim();
    }
    if (message.sequence !== "0") {
      writer.uint32(24).uint64(message.sequence);
    }
    for (const v of message.buffers) {
      Buffer.encode(v!, writer.uint32(34).fork()).ldelim();
    }
    if (message.writerEpoch !== "0") {
      writer.uint32(40).uint64(message.writerEpoch);
    }
    if (message.eof === true) {
      writer.uint32(48).bool(message.eof);
    }
    if (message.finalSequence !== "0") {
      writer.uint32(56).uint64(message.finalSequence);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): StreamRecord {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseStreamRecord();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 2:
          if (tag !== 18) {
            break;
          }

          message.timestamp = fromTimestamp(Timestamp.decode(reader, reader.uint32()));
          continue;
        case 3:
          if (tag !== 24) {
            break;
          }

          message.sequence = longToString(reader.uint64() as Long);
          continue;
        case 4:
          if (tag !== 34) {
            break;
          }

          message.buffers.push(Buffer.decode(reader, reader.uint32()));
          continue;
        case 5:
          if (tag !== 40) {
            break;
          }

          message.writerEpoch = longToString(reader.uint64() as Long);
          continue;
        case 6:
          if (tag !== 48) {
            break;
          }

          message.eof = reader.bool();
          continue;
        case 7:
          if (tag !== 56) {
            break;
          }

          message.finalSequence = longToString(reader.uint64() as Long);
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): StreamRecord {
    return {
      timestamp: isSet(object.timestamp) ? fromJsonTimestamp(object.timestamp) : undefined,
      sequence: isSet(object.sequence) ? globalThis.String(object.sequence) : "0",
      buffers: globalThis.Array.isArray(object?.buffers) ? object.buffers.map((e: any) => Buffer.fromJSON(e)) : [],
      writerEpoch: isSet(object.writerEpoch) ? globalThis.String(object.writerEpoch) : "0",
      eof: isSet(object.eof) ? globalThis.Boolean(object.eof) : false,
      finalSequence: isSet(object.finalSequence) ? globalThis.String(object.finalSequence) : "0",
    };
  },

  toJSON(message: StreamRecord): unknown {
    const obj: any = {};
    if (message.timestamp !== undefined) {
      obj.timestamp = message.timestamp.toISOString();
    }
    if (message.sequence !== "0") {
      obj.sequence = message.sequence;
    }
    if (message.buffers?.length) {
      obj.buffers = message.buffers.map((e) => Buffer.toJSON(e));
    }
    if (message.writerEpoch !== "0") {
      obj.writerEpoch = message.writerEpoch;
    }
    if (message.eof === true) {
      obj.eof = message.eof;
    }
    if (message.finalSequence !== "0") {
      obj.finalSequence = message.finalSequence;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<StreamRecord>, I>>(base?: I): StreamRecord {
    return StreamRecord.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<StreamRecord>, I>>(object: I): StreamRecord {
    const message = createBaseStreamRecord();
    message.timestamp = object.timestamp ?? undefined;
    message.sequence = object.sequence ?? "0";
    message.buffers = object.buffers?.map((e) => Buffer.fromPartial(e)) || [];
    message.writerEpoch = object.writerEpoch ?? "0";
    message.eof = object.eof ?? false;
    message.finalSequence = object.finalSequence ?? "0";
    return message;
  },
};

function createBaseBuffer(): Buffer {
  return { timestamp: undefined, data: new Uint8Array(0), source: 0, record: undefined };
}

export const Buffer = {
  encode(message: Buffer, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.timestamp !== undefined) {
      Timestamp.encode(toTimestamp(message.timestamp), writer.uint32(10).fork()).ldelim();
    }
    if (message.data.length !== 0) {
      writer.uint32(18).bytes(message.data);
    }
    if (message.source !== 0) {
      writer.uint32(24).int32(message.source);
    }
    if (message.record !== undefined) {
      LogRecord.encode(message.record, writer.uint32(34).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): Buffer {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseBuffer();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.timestamp = fromTimestamp(Timestamp.decode(reader, reader.uint32()));
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.data = reader.bytes();
          continue;
        case 3:
          if (tag !== 24) {
            break;
          }

          message.source = reader.int32() as any;
          continue;
        case 4:
          if (tag !== 34) {
            break;
          }

          message.record = LogRecord.decode(reader, reader.uint32());
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): Buffer {
    return {
      timestamp: isSet(object.timestamp) ? fromJsonTimestamp(object.timestamp) : undefined,
      data: isSet(object.data) ? bytesFromBase64(object.data) : new Uint8Array(0),
      source: isSet(object.source) ? bufferSourceFromJSON(object.source) : 0,
      record: isSet(object.record) ? LogRecord.fromJSON(object.record) : undefined,
    };
  },

  toJSON(message: Buffer): unknown {
    const obj: any = {};
    if (message.timestamp !== undefined) {
      obj.timestamp = message.timestamp.toISOString();
    }
    if (message.data.length !== 0) {
      obj.data = base64FromBytes(message.data);
    }
    if (message.source !== 0) {
      obj.source = bufferSourceToJSON(message.source);
    }
    if (message.record !== undefined) {
      obj.record = LogRecord.toJSON(message.record);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<Buffer>, I>>(base?: I): Buffer {
    return Buffer.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<Buffer>, I>>(object: I): Buffer {
    const message = createBaseBuffer();
    message.timestamp = object.timestamp ?? undefined;
    message.data = object.data ?? new Uint8Array(0);
    message.source = object.source ?? 0;
    message.record = (object.record !== undefined && object.record !== null)
      ? LogRecord.fromPartial(object.record)
      : undefined;
    return message;
  },
};

function createBaseLogRecord(): LogRecord {
  return {
    level: 0,
    caller: "",
    goid: "0",
    message: "",
    extraData: {},
    rendered: "",
    droppedRecords: "0",
    error: undefined,
    thread: "",
    processRunUid: "",
  };
}

export const LogRecord = {
  encode(message: LogRecord, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.level !== 0) {
      writer.uint32(8).int32(message.level);
    }
    if (message.caller !== "") {
      writer.uint32(18).string(message.caller);
    }
    if (message.goid !== "0") {
      writer.uint32(24).uint64(message.goid);
    }
    if (message.message !== "") {
      writer.uint32(34).string(message.message);
    }
    Object.entries(message.extraData).forEach(([key, value]) => {
      LogRecord_ExtraDataEntry.encode({ key: key as any, value }, writer.uint32(42).fork()).ldelim();
    });
    if (message.rendered !== "") {
      writer.uint32(58).string(message.rendered);
    }
    if (message.droppedRecords !== "0") {
      writer.uint32(64).uint64(message.droppedRecords);
    }
    if (message.error !== undefined) {
      LogError.encode(message.error, writer.uint32(74).fork()).ldelim();
    }
    if (message.thread !== "") {
      writer.uint32(82).string(message.thread);
    }
    if (message.processRunUid !== "") {
      writer.uint32(90).string(message.processRunUid);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): LogRecord {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseLogRecord();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 8) {
            break;
          }

          message.level = reader.int32() as any;
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.caller = reader.string();
          continue;
        case 3:
          if (tag !== 24) {
            break;
          }

          message.goid = longToString(reader.uint64() as Long);
          continue;
        case 4:
          if (tag !== 34) {
            break;
          }

          message.message = reader.string();
          continue;
        case 5:
          if (tag !== 42) {
            break;
          }

          const entry5 = LogRecord_ExtraDataEntry.decode(reader, reader.uint32());
          if (entry5.value !== undefined) {
            message.extraData[entry5.key] = entry5.value;
          }
          continue;
        case 7:
          if (tag !== 58) {
            break;
          }

          message.rendered = reader.string();
          continue;
        case 8:
          if (tag !== 64) {
            break;
          }

          message.droppedRecords = longToString(reader.uint64() as Long);
          continue;
        case 9:
          if (tag !== 74) {
            break;
          }

          message.error = LogError.decode(reader, reader.uint32());
          continue;
        case 10:
          if (tag !== 82) {
            break;
          }

          message.thread = reader.string();
          continue;
        case 11:
          if (tag !== 90) {
            break;
          }

          message.processRunUid = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): LogRecord {
    return {
      level: isSet(object.level) ? logLevelFromJSON(object.level) : 0,
      caller: isSet(object.caller) ? globalThis.String(object.caller) : "",
      goid: isSet(object.goid) ? globalThis.String(object.goid) : "0",
      message: isSet(object.message) ? globalThis.String(object.message) : "",
      extraData: isObject(object.extraData)
        ? Object.entries(object.extraData).reduce<{ [key: string]: string }>((acc, [key, value]) => {
          acc[key] = String(value);
          return acc;
        }, {})
        : {},
      rendered: isSet(object.rendered) ? globalThis.String(object.rendered) : "",
      droppedRecords: isSet(object.droppedRecords) ? globalThis.String(object.droppedRecords) : "0",
      error: isSet(object.error) ? LogError.fromJSON(object.error) : undefined,
      thread: isSet(object.thread) ? globalThis.String(object.thread) : "",
      processRunUid: isSet(object.processRunUid) ? globalThis.String(object.processRunUid) : "",
    };
  },

  toJSON(message: LogRecord): unknown {
    const obj: any = {};
    if (message.level !== 0) {
      obj.level = logLevelToJSON(message.level);
    }
    if (message.caller !== "") {
      obj.caller = message.caller;
    }
    if (message.goid !== "0") {
      obj.goid = message.goid;
    }
    if (message.message !== "") {
      obj.message = message.message;
    }
    if (message.extraData) {
      const entries = Object.entries(message.extraData);
      if (entries.length > 0) {
        obj.extraData = {};
        entries.forEach(([k, v]) => {
          obj.extraData[k] = v;
        });
      }
    }
    if (message.rendered !== "") {
      obj.rendered = message.rendered;
    }
    if (message.droppedRecords !== "0") {
      obj.droppedRecords = message.droppedRecords;
    }
    if (message.error !== undefined) {
      obj.error = LogError.toJSON(message.error);
    }
    if (message.thread !== "") {
      obj.thread = message.thread;
    }
    if (message.processRunUid !== "") {
      obj.processRunUid = message.processRunUid;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<LogRecord>, I>>(base?: I): LogRecord {
    return LogRecord.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<LogRecord>, I>>(object: I): LogRecord {
    const message = createBaseLogRecord();
    message.level = object.level ?? 0;
    message.caller = object.caller ?? "";
    message.goid = object.goid ?? "0";
    message.message = object.message ?? "";
    message.extraData = Object.entries(object.extraData ?? {}).reduce<{ [key: string]: string }>(
      (acc, [key, value]) => {
        if (value !== undefined) {
          acc[key] = globalThis.String(value);
        }
        return acc;
      },
      {},
    );
    message.rendered = object.rendered ?? "";
    message.droppedRecords = object.droppedRecords ?? "0";
    message.error = (object.error !== undefined && object.error !== null)
      ? LogError.fromPartial(object.error)
      : undefined;
    message.thread = object.thread ?? "";
    message.processRunUid = object.processRunUid ?? "";
    return message;
  },
};

function createBaseLogRecord_ExtraDataEntry(): LogRecord_ExtraDataEntry {
  return { key: "", value: "" };
}

export const LogRecord_ExtraDataEntry = {
  encode(message: LogRecord_ExtraDataEntry, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.key !== "") {
      writer.uint32(10).string(message.key);
    }
    if (message.value !== "") {
      writer.uint32(18).string(message.value);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): LogRecord_ExtraDataEntry {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseLogRecord_ExtraDataEntry();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.key = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.value = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): LogRecord_ExtraDataEntry {
    return {
      key: isSet(object.key) ? globalThis.String(object.key) : "",
      value: isSet(object.value) ? globalThis.String(object.value) : "",
    };
  },

  toJSON(message: LogRecord_ExtraDataEntry): unknown {
    const obj: any = {};
    if (message.key !== "") {
      obj.key = message.key;
    }
    if (message.value !== "") {
      obj.value = message.value;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<LogRecord_ExtraDataEntry>, I>>(base?: I): LogRecord_ExtraDataEntry {
    return LogRecord_ExtraDataEntry.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<LogRecord_ExtraDataEntry>, I>>(object: I): LogRecord_ExtraDataEntry {
    const message = createBaseLogRecord_ExtraDataEntry();
    message.key = object.key ?? "";
    message.value = object.value ?? "";
    return message;
  },
};

function createBaseLogError(): LogError {
  return { message: "", type: "", stack: "", cause: undefined };
}

export const LogError = {
  encode(message: LogError, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.message !== "") {
      writer.uint32(10).string(message.message);
    }
    if (message.type !== "") {
      writer.uint32(18).string(message.type);
    }
    if (message.stack !== "") {
      writer.uint32(26).string(message.stack);
    }
    if (message.cause !== undefined) {
      LogError.encode(message.cause, writer.uint32(34).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): LogError {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseLogError();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.message = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.type = reader.string();
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.stack = reader.string();
          continue;
        case 4:
          if (tag !== 34) {
            break;
          }

          message.cause = LogError.decode(reader, reader.uint32());
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): LogError {
    return {
      message: isSet(object.message) ? globalThis.String(object.message) : "",
      type: isSet(object.type) ? globalThis.String(object.type) : "",
      stack: isSet(object.stack) ? globalThis.String(object.stack) : "",
      cause: isSet(object.cause) ? LogError.fromJSON(object.cause) : undefined,
    };
  },

  toJSON(message: LogError): unknown {
    const obj: any = {};
    if (message.message !== "") {
      obj.message = message.message;
    }
    if (message.type !== "") {
      obj.type = message.type;
    }
    if (message.stack !== "") {
      obj.stack = message.stack;
    }
    if (message.cause !== undefined) {
      obj.cause = LogError.toJSON(message.cause);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<LogError>, I>>(base?: I): LogError {
    return LogError.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<LogError>, I>>(object: I): LogError {
    const message = createBaseLogError();
    message.message = object.message ?? "";
    message.type = object.type ?? "";
    message.stack = object.stack ?? "";
    message.cause = (object.cause !== undefined && object.cause !== null)
      ? LogError.fromPartial(object.cause)
      : undefined;
    return message;
  },
};

function createBaseListServicesRequest(): ListServicesRequest {
  return { workerUid: "" };
}

export const ListServicesRequest = {
  encode(message: ListServicesRequest, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.workerUid !== "") {
      writer.uint32(10).string(message.workerUid);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): ListServicesRequest {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseListServicesRequest();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.workerUid = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): ListServicesRequest {
    return { workerUid: isSet(object.workerUid) ? globalThis.String(object.workerUid) : "" };
  },

  toJSON(message: ListServicesRequest): unknown {
    const obj: any = {};
    if (message.workerUid !== "") {
      obj.workerUid = message.workerUid;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<ListServicesRequest>, I>>(base?: I): ListServicesRequest {
    return ListServicesRequest.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<ListServicesRequest>, I>>(object: I): ListServicesRequest {
    const message = createBaseListServicesRequest();
    message.workerUid = object.workerUid ?? "";
    return message;
  },
};

function createBaseListServicesResponse(): ListServicesResponse {
  return { records: [] };
}

export const ListServicesResponse = {
  encode(message: ListServicesResponse, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    for (const v of message.records) {
      ServiceRecord.encode(v!, writer.uint32(10).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): ListServicesResponse {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseListServicesResponse();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.records.push(ServiceRecord.decode(reader, reader.uint32()));
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): ListServicesResponse {
    return {
      records: globalThis.Array.isArray(object?.records)
        ? object.records.map((e: any) => ServiceRecord.fromJSON(e))
        : [],
    };
  },

  toJSON(message: ListServicesResponse): unknown {
    const obj: any = {};
    if (message.records?.length) {
      obj.records = message.records.map((e) => ServiceRecord.toJSON(e));
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<ListServicesResponse>, I>>(base?: I): ListServicesResponse {
    return ListServicesResponse.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<ListServicesResponse>, I>>(object: I): ListServicesResponse {
    const message = createBaseListServicesResponse();
    message.records = object.records?.map((e) => ServiceRecord.fromPartial(e)) || [];
    return message;
  },
};

function createBaseServiceRecord(): ServiceRecord {
  return { uid: "", name: "", workerUid: "", workerEnabled: false, unitJson: undefined };
}

export const ServiceRecord = {
  encode(message: ServiceRecord, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.uid !== "") {
      writer.uint32(10).string(message.uid);
    }
    if (message.name !== "") {
      writer.uint32(18).string(message.name);
    }
    if (message.workerUid !== "") {
      writer.uint32(26).string(message.workerUid);
    }
    if (message.workerEnabled === true) {
      writer.uint32(32).bool(message.workerEnabled);
    }
    if (message.unitJson !== undefined) {
      ServiceUnitJson.encode(message.unitJson, writer.uint32(42).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): ServiceRecord {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseServiceRecord();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.uid = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.name = reader.string();
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.workerUid = reader.string();
          continue;
        case 4:
          if (tag !== 32) {
            break;
          }

          message.workerEnabled = reader.bool();
          continue;
        case 5:
          if (tag !== 42) {
            break;
          }

          message.unitJson = ServiceUnitJson.decode(reader, reader.uint32());
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): ServiceRecord {
    return {
      uid: isSet(object.uid) ? globalThis.String(object.uid) : "",
      name: isSet(object.name) ? globalThis.String(object.name) : "",
      workerUid: isSet(object.workerUid) ? globalThis.String(object.workerUid) : "",
      workerEnabled: isSet(object.workerEnabled) ? globalThis.Boolean(object.workerEnabled) : false,
      unitJson: isSet(object.unitJson) ? ServiceUnitJson.fromJSON(object.unitJson) : undefined,
    };
  },

  toJSON(message: ServiceRecord): unknown {
    const obj: any = {};
    if (message.uid !== "") {
      obj.uid = message.uid;
    }
    if (message.name !== "") {
      obj.name = message.name;
    }
    if (message.workerUid !== "") {
      obj.workerUid = message.workerUid;
    }
    if (message.workerEnabled === true) {
      obj.workerEnabled = message.workerEnabled;
    }
    if (message.unitJson !== undefined) {
      obj.unitJson = ServiceUnitJson.toJSON(message.unitJson);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<ServiceRecord>, I>>(base?: I): ServiceRecord {
    return ServiceRecord.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<ServiceRecord>, I>>(object: I): ServiceRecord {
    const message = createBaseServiceRecord();
    message.uid = object.uid ?? "";
    message.name = object.name ?? "";
    message.workerUid = object.workerUid ?? "";
    message.workerEnabled = object.workerEnabled ?? false;
    message.unitJson = (object.unitJson !== undefined && object.unitJson !== null)
      ? ServiceUnitJson.fromPartial(object.unitJson)
      : undefined;
    return message;
  },
};

function createBasePingRequest(): PingRequest {
  return { uid: "", payload: "" };
}

export const PingRequest = {
  encode(message: PingRequest, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.uid !== "") {
      writer.uint32(10).string(message.uid);
    }
    if (message.payload !== "") {
      writer.uint32(18).string(message.payload);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): PingRequest {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBasePingRequest();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.uid = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.payload = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): PingRequest {
    return {
      uid: isSet(object.uid) ? globalThis.String(object.uid) : "",
      payload: isSet(object.payload) ? globalThis.String(object.payload) : "",
    };
  },

  toJSON(message: PingRequest): unknown {
    const obj: any = {};
    if (message.uid !== "") {
      obj.uid = message.uid;
    }
    if (message.payload !== "") {
      obj.payload = message.payload;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<PingRequest>, I>>(base?: I): PingRequest {
    return PingRequest.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<PingRequest>, I>>(object: I): PingRequest {
    const message = createBasePingRequest();
    message.uid = object.uid ?? "";
    message.payload = object.payload ?? "";
    return message;
  },
};

function createBaseServiceUnitJson(): ServiceUnitJson {
  return { status: 0, statusSource: "", unitExists: false, unitDescription: "", unitName: "" };
}

export const ServiceUnitJson = {
  encode(message: ServiceUnitJson, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.status !== 0) {
      writer.uint32(8).int32(message.status);
    }
    if (message.statusSource !== "") {
      writer.uint32(18).string(message.statusSource);
    }
    if (message.unitExists === true) {
      writer.uint32(24).bool(message.unitExists);
    }
    if (message.unitDescription !== "") {
      writer.uint32(34).string(message.unitDescription);
    }
    if (message.unitName !== "") {
      writer.uint32(42).string(message.unitName);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): ServiceUnitJson {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseServiceUnitJson();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 8) {
            break;
          }

          message.status = reader.int32() as any;
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.statusSource = reader.string();
          continue;
        case 3:
          if (tag !== 24) {
            break;
          }

          message.unitExists = reader.bool();
          continue;
        case 4:
          if (tag !== 34) {
            break;
          }

          message.unitDescription = reader.string();
          continue;
        case 5:
          if (tag !== 42) {
            break;
          }

          message.unitName = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): ServiceUnitJson {
    return {
      status: isSet(object.status) ? systemdServiceStatusFromJSON(object.status) : 0,
      statusSource: isSet(object.statusSource) ? globalThis.String(object.statusSource) : "",
      unitExists: isSet(object.unitExists) ? globalThis.Boolean(object.unitExists) : false,
      unitDescription: isSet(object.unitDescription) ? globalThis.String(object.unitDescription) : "",
      unitName: isSet(object.unitName) ? globalThis.String(object.unitName) : "",
    };
  },

  toJSON(message: ServiceUnitJson): unknown {
    const obj: any = {};
    if (message.status !== 0) {
      obj.status = systemdServiceStatusToJSON(message.status);
    }
    if (message.statusSource !== "") {
      obj.statusSource = message.statusSource;
    }
    if (message.unitExists === true) {
      obj.unitExists = message.unitExists;
    }
    if (message.unitDescription !== "") {
      obj.unitDescription = message.unitDescription;
    }
    if (message.unitName !== "") {
      obj.unitName = message.unitName;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<ServiceUnitJson>, I>>(base?: I): ServiceUnitJson {
    return ServiceUnitJson.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<ServiceUnitJson>, I>>(object: I): ServiceUnitJson {
    const message = createBaseServiceUnitJson();
    message.status = object.status ?? 0;
    message.statusSource = object.statusSource ?? "";
    message.unitExists = object.unitExists ?? false;
    message.unitDescription = object.unitDescription ?? "";
    message.unitName = object.unitName ?? "";
    return message;
  },
};

function createBaseResolveJobUidRequest(): ResolveJobUidRequest {
  return { jobName: "", workerUid: "", kind: "" };
}

export const ResolveJobUidRequest = {
  encode(message: ResolveJobUidRequest, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.jobName !== "") {
      writer.uint32(10).string(message.jobName);
    }
    if (message.workerUid !== "") {
      writer.uint32(18).string(message.workerUid);
    }
    if (message.kind !== "") {
      writer.uint32(26).string(message.kind);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): ResolveJobUidRequest {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseResolveJobUidRequest();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.jobName = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.workerUid = reader.string();
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.kind = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): ResolveJobUidRequest {
    return {
      jobName: isSet(object.jobName) ? globalThis.String(object.jobName) : "",
      workerUid: isSet(object.workerUid) ? globalThis.String(object.workerUid) : "",
      kind: isSet(object.kind) ? globalThis.String(object.kind) : "",
    };
  },

  toJSON(message: ResolveJobUidRequest): unknown {
    const obj: any = {};
    if (message.jobName !== "") {
      obj.jobName = message.jobName;
    }
    if (message.workerUid !== "") {
      obj.workerUid = message.workerUid;
    }
    if (message.kind !== "") {
      obj.kind = message.kind;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<ResolveJobUidRequest>, I>>(base?: I): ResolveJobUidRequest {
    return ResolveJobUidRequest.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<ResolveJobUidRequest>, I>>(object: I): ResolveJobUidRequest {
    const message = createBaseResolveJobUidRequest();
    message.jobName = object.jobName ?? "";
    message.workerUid = object.workerUid ?? "";
    message.kind = object.kind ?? "";
    return message;
  },
};

function createBaseResolveJobUidResponse(): ResolveJobUidResponse {
  return { jobUid: "" };
}

export const ResolveJobUidResponse = {
  encode(message: ResolveJobUidResponse, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.jobUid !== "") {
      writer.uint32(10).string(message.jobUid);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): ResolveJobUidResponse {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseResolveJobUidResponse();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.jobUid = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): ResolveJobUidResponse {
    return { jobUid: isSet(object.jobUid) ? globalThis.String(object.jobUid) : "" };
  },

  toJSON(message: ResolveJobUidResponse): unknown {
    const obj: any = {};
    if (message.jobUid !== "") {
      obj.jobUid = message.jobUid;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<ResolveJobUidResponse>, I>>(base?: I): ResolveJobUidResponse {
    return ResolveJobUidResponse.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<ResolveJobUidResponse>, I>>(object: I): ResolveJobUidResponse {
    const message = createBaseResolveJobUidResponse();
    message.jobUid = object.jobUid ?? "";
    return message;
  },
};

function createBaseAuthorizeForJobRequest(): AuthorizeForJobRequest {
  return { callerMailbox: "", jobUid: "", requiredRank: 0 };
}

export const AuthorizeForJobRequest = {
  encode(message: AuthorizeForJobRequest, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.callerMailbox !== "") {
      writer.uint32(10).string(message.callerMailbox);
    }
    if (message.jobUid !== "") {
      writer.uint32(18).string(message.jobUid);
    }
    if (message.requiredRank !== 0) {
      writer.uint32(24).int32(message.requiredRank);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): AuthorizeForJobRequest {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseAuthorizeForJobRequest();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.callerMailbox = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.jobUid = reader.string();
          continue;
        case 3:
          if (tag !== 24) {
            break;
          }

          message.requiredRank = reader.int32();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): AuthorizeForJobRequest {
    return {
      callerMailbox: isSet(object.callerMailbox) ? globalThis.String(object.callerMailbox) : "",
      jobUid: isSet(object.jobUid) ? globalThis.String(object.jobUid) : "",
      requiredRank: isSet(object.requiredRank) ? globalThis.Number(object.requiredRank) : 0,
    };
  },

  toJSON(message: AuthorizeForJobRequest): unknown {
    const obj: any = {};
    if (message.callerMailbox !== "") {
      obj.callerMailbox = message.callerMailbox;
    }
    if (message.jobUid !== "") {
      obj.jobUid = message.jobUid;
    }
    if (message.requiredRank !== 0) {
      obj.requiredRank = Math.round(message.requiredRank);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<AuthorizeForJobRequest>, I>>(base?: I): AuthorizeForJobRequest {
    return AuthorizeForJobRequest.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<AuthorizeForJobRequest>, I>>(object: I): AuthorizeForJobRequest {
    const message = createBaseAuthorizeForJobRequest();
    message.callerMailbox = object.callerMailbox ?? "";
    message.jobUid = object.jobUid ?? "";
    message.requiredRank = object.requiredRank ?? 0;
    return message;
  },
};

function createBaseAuthorizeForJobResponse(): AuthorizeForJobResponse {
  return { allowed: false, reason: "" };
}

export const AuthorizeForJobResponse = {
  encode(message: AuthorizeForJobResponse, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.allowed === true) {
      writer.uint32(8).bool(message.allowed);
    }
    if (message.reason !== "") {
      writer.uint32(18).string(message.reason);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): AuthorizeForJobResponse {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseAuthorizeForJobResponse();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 8) {
            break;
          }

          message.allowed = reader.bool();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.reason = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): AuthorizeForJobResponse {
    return {
      allowed: isSet(object.allowed) ? globalThis.Boolean(object.allowed) : false,
      reason: isSet(object.reason) ? globalThis.String(object.reason) : "",
    };
  },

  toJSON(message: AuthorizeForJobResponse): unknown {
    const obj: any = {};
    if (message.allowed === true) {
      obj.allowed = message.allowed;
    }
    if (message.reason !== "") {
      obj.reason = message.reason;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<AuthorizeForJobResponse>, I>>(base?: I): AuthorizeForJobResponse {
    return AuthorizeForJobResponse.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<AuthorizeForJobResponse>, I>>(object: I): AuthorizeForJobResponse {
    const message = createBaseAuthorizeForJobResponse();
    message.allowed = object.allowed ?? false;
    message.reason = object.reason ?? "";
    return message;
  },
};

function createBaseArchivedNatsMessage(): ArchivedNatsMessage {
  return { sequence: "0", timestamp: undefined, subject: "", data: new Uint8Array(0), headers: {} };
}

export const ArchivedNatsMessage = {
  encode(message: ArchivedNatsMessage, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.sequence !== "0") {
      writer.uint32(8).uint64(message.sequence);
    }
    if (message.timestamp !== undefined) {
      Timestamp.encode(toTimestamp(message.timestamp), writer.uint32(18).fork()).ldelim();
    }
    if (message.subject !== "") {
      writer.uint32(26).string(message.subject);
    }
    if (message.data.length !== 0) {
      writer.uint32(34).bytes(message.data);
    }
    Object.entries(message.headers).forEach(([key, value]) => {
      ArchivedNatsMessage_HeadersEntry.encode({ key: key as any, value }, writer.uint32(42).fork()).ldelim();
    });
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): ArchivedNatsMessage {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseArchivedNatsMessage();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 8) {
            break;
          }

          message.sequence = longToString(reader.uint64() as Long);
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.timestamp = fromTimestamp(Timestamp.decode(reader, reader.uint32()));
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.subject = reader.string();
          continue;
        case 4:
          if (tag !== 34) {
            break;
          }

          message.data = reader.bytes();
          continue;
        case 5:
          if (tag !== 42) {
            break;
          }

          const entry5 = ArchivedNatsMessage_HeadersEntry.decode(reader, reader.uint32());
          if (entry5.value !== undefined) {
            message.headers[entry5.key] = entry5.value;
          }
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): ArchivedNatsMessage {
    return {
      sequence: isSet(object.sequence) ? globalThis.String(object.sequence) : "0",
      timestamp: isSet(object.timestamp) ? fromJsonTimestamp(object.timestamp) : undefined,
      subject: isSet(object.subject) ? globalThis.String(object.subject) : "",
      data: isSet(object.data) ? bytesFromBase64(object.data) : new Uint8Array(0),
      headers: isObject(object.headers)
        ? Object.entries(object.headers).reduce<{ [key: string]: string }>((acc, [key, value]) => {
          acc[key] = String(value);
          return acc;
        }, {})
        : {},
    };
  },

  toJSON(message: ArchivedNatsMessage): unknown {
    const obj: any = {};
    if (message.sequence !== "0") {
      obj.sequence = message.sequence;
    }
    if (message.timestamp !== undefined) {
      obj.timestamp = message.timestamp.toISOString();
    }
    if (message.subject !== "") {
      obj.subject = message.subject;
    }
    if (message.data.length !== 0) {
      obj.data = base64FromBytes(message.data);
    }
    if (message.headers) {
      const entries = Object.entries(message.headers);
      if (entries.length > 0) {
        obj.headers = {};
        entries.forEach(([k, v]) => {
          obj.headers[k] = v;
        });
      }
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<ArchivedNatsMessage>, I>>(base?: I): ArchivedNatsMessage {
    return ArchivedNatsMessage.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<ArchivedNatsMessage>, I>>(object: I): ArchivedNatsMessage {
    const message = createBaseArchivedNatsMessage();
    message.sequence = object.sequence ?? "0";
    message.timestamp = object.timestamp ?? undefined;
    message.subject = object.subject ?? "";
    message.data = object.data ?? new Uint8Array(0);
    message.headers = Object.entries(object.headers ?? {}).reduce<{ [key: string]: string }>((acc, [key, value]) => {
      if (value !== undefined) {
        acc[key] = globalThis.String(value);
      }
      return acc;
    }, {});
    return message;
  },
};

function createBaseArchivedNatsMessage_HeadersEntry(): ArchivedNatsMessage_HeadersEntry {
  return { key: "", value: "" };
}

export const ArchivedNatsMessage_HeadersEntry = {
  encode(message: ArchivedNatsMessage_HeadersEntry, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.key !== "") {
      writer.uint32(10).string(message.key);
    }
    if (message.value !== "") {
      writer.uint32(18).string(message.value);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): ArchivedNatsMessage_HeadersEntry {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseArchivedNatsMessage_HeadersEntry();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.key = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.value = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): ArchivedNatsMessage_HeadersEntry {
    return {
      key: isSet(object.key) ? globalThis.String(object.key) : "",
      value: isSet(object.value) ? globalThis.String(object.value) : "",
    };
  },

  toJSON(message: ArchivedNatsMessage_HeadersEntry): unknown {
    const obj: any = {};
    if (message.key !== "") {
      obj.key = message.key;
    }
    if (message.value !== "") {
      obj.value = message.value;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<ArchivedNatsMessage_HeadersEntry>, I>>(
    base?: I,
  ): ArchivedNatsMessage_HeadersEntry {
    return ArchivedNatsMessage_HeadersEntry.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<ArchivedNatsMessage_HeadersEntry>, I>>(
    object: I,
  ): ArchivedNatsMessage_HeadersEntry {
    const message = createBaseArchivedNatsMessage_HeadersEntry();
    message.key = object.key ?? "";
    message.value = object.value ?? "";
    return message;
  },
};

function bytesFromBase64(b64: string): Uint8Array {
  if (globalThis.Buffer) {
    return Uint8Array.from(globalThis.Buffer.from(b64, "base64"));
  } else {
    const bin = globalThis.atob(b64);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; ++i) {
      arr[i] = bin.charCodeAt(i);
    }
    return arr;
  }
}

function base64FromBytes(arr: Uint8Array): string {
  if (globalThis.Buffer) {
    return globalThis.Buffer.from(arr).toString("base64");
  } else {
    const bin: string[] = [];
    arr.forEach((byte) => {
      bin.push(globalThis.String.fromCharCode(byte));
    });
    return globalThis.btoa(bin.join(""));
  }
}

type Builtin = Date | Function | Uint8Array | string | number | boolean | undefined;

export type DeepPartial<T> = T extends Builtin ? T
  : T extends globalThis.Array<infer U> ? globalThis.Array<DeepPartial<U>>
  : T extends ReadonlyArray<infer U> ? ReadonlyArray<DeepPartial<U>>
  : T extends {} ? { [K in keyof T]?: DeepPartial<T[K]> }
  : Partial<T>;

type KeysOfUnion<T> = T extends T ? keyof T : never;
export type Exact<P, I extends P> = P extends Builtin ? P
  : P & { [K in keyof P]: Exact<P[K], I[K]> } & { [K in Exclude<keyof I, KeysOfUnion<P>>]: never };

function toTimestamp(date: Date): Timestamp {
  const seconds = Math.trunc(date.getTime() / 1_000).toString();
  const nanos = (date.getTime() % 1_000) * 1_000_000;
  return { seconds, nanos };
}

function fromTimestamp(t: Timestamp): Date {
  let millis = (globalThis.Number(t.seconds) || 0) * 1_000;
  millis += (t.nanos || 0) / 1_000_000;
  return new globalThis.Date(millis);
}

function fromJsonTimestamp(o: any): Date {
  if (o instanceof globalThis.Date) {
    return o;
  } else if (typeof o === "string") {
    return new globalThis.Date(o);
  } else {
    return fromTimestamp(Timestamp.fromJSON(o));
  }
}

function longToString(long: Long) {
  return long.toString();
}

if (_m0.util.Long !== Long) {
  _m0.util.Long = Long as any;
  _m0.configure();
}

function isObject(value: any): boolean {
  return typeof value === "object" && value !== null;
}

function isSet(value: any): boolean {
  return value !== null && value !== undefined;
}
